import { Stack } from '@mui/material';
import { FormTitle, HomePageFormLink } from 'Components';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { FirstSection } from './FirstSection';
import { AddCaseDetail } from './AddCaseDetail';
import { SecondSection } from './SecondSection';
import { ThirdSection } from './ThirdSection';
import { TopDescription } from './TopDescription';
import { DeleteModal, Text } from './DeleteModal';
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useFieldArray,
} from 'react-hook-form';
import {
  caseDetailsEmptyObject,
  deleteCaseDetails,
  getCaseDetails,
  ICaseDetails,
  ICaseDetailsInputs,
  postCaseDetails,
  putCaseDetails,
} from 'API';
import {
  Fragment,
  useEffect,
  useRef,
  useState,
  createContext,
  useContext,
} from 'react';
import { diseaseId, IndexProvider } from './CaseContext';
import { debounce, files, initFocus, handleValidationErrors } from 'Utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { caseDetailsSchema } from './Schema';
import { format } from 'date-fns';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { RoutesConfig } from 'Routes';
import { ClassifiedDocumentsDeclaration } from './ClassifiedDocumentsDeclaration';
import { FormSectionWrapper } from 'Components/FormSectionWrapper';
import { FourthSection } from './FourthSection';
import { FifthSectionNew } from './FifthSectionNew';
import { SixthSectionNew } from './SixthSectionNew';
import { useIsDesktop } from 'Hooks';
import { convertStringBoolToBoolean } from 'Utils/BooleanStringHandler';
import { AnotherCaseDetailHeader } from './AnotherCaseDetailHeader';

export type Inputs = ICaseDetailsInputs;

interface ValidationErrorContextType {
  registerValidationErrorHandler: (
    caseIndex: number,
    handler: () => void
  ) => void;
  unregisterValidationErrorHandler: (caseIndex: number) => void;
}

const ValidationErrorContext = createContext<ValidationErrorContextType | null>(
  null
);

export const useValidationErrorContext = () => {
  const context = useContext(ValidationErrorContext);
  if (!context) {
    throw new Error(
      'useValidationErrorContext must be used within ValidationErrorProvider'
    );
  }
  return context;
};

export const CaseDetails = () => {
  const defaultValues = {
    caseDetails: [
      {
        ...caseDetailsEmptyObject,
        id: 0,
      },
    ],
  };

  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [indexForm, setIndexForm] = useState<number>(0);
  const [fieldIdForm, setFieldIdForm] = useState(0);

  const validationErrorHandlers = useRef<Map<number, () => void>>(new Map());

  const methods = useForm<Inputs>({
    mode: 'onBlur',
    resolver: yupResolver(caseDetailsSchema),
    defaultValues,
    context: {},
  });

  const { append, remove, fields } = useFieldArray({
    name: 'caseDetails',
    control: methods.control,
    keyName: 'rid',
  });

  const navigate = useNavigate();
  const isDesktop = useIsDesktop();

  const registerValidationErrorHandler = (
    caseIndex: number,
    handler: () => void
  ) => {
    validationErrorHandlers.current.set(caseIndex, handler);
  };

  const unregisterValidationErrorHandler = (caseIndex: number) => {
    validationErrorHandlers.current.delete(caseIndex);
  };

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    await forceSubmit();
    const result = await files();
    if (result) {
      navigate(RoutesConfig.STATUS_LOBBY.PATH);
    }
  };

  const handleValidationErrorsLocal = async () => {
    await files();
    handleValidationErrors(() => {
      validationErrorHandlers.current.forEach((handler, caseIndex) => {
        handler();
      });
    });
  };

  const removeHandler = async (index: number, id: number) => {
    initFlag.current = true;
    const caseDetails = await deleteCaseDetails(id);
    if (caseDetails) {
      remove(index);
      setShowDeleteModal(false);
    }
    initFlag.current = false;
  };

  const appendHandler = async () => {
    initFlag.current = true;
    const caseDetails = await postCaseDetails({});
    if (caseDetails) {
      append({
        ...caseDetailsEmptyObject,
        hospitalizationDetailsSection: [],
        id: caseDetails.id,
      });
    }
    initFlag.current = false;
  };

  const initFlag = useRef<boolean>(true);

  const initForm = async () => {
    initFlag.current = true;
    const caseDetails = await getCaseDetails();
    if (caseDetails && caseDetails?.length) {
      methods.reset({ caseDetails: [...caseDetails] });
    } else {
      const caseDetails = await postCaseDetails({});
      if (caseDetails) {
        methods.reset({ caseDetails: [caseDetails] });
      }
    }
    initFlag.current = false;
  };

  const onDeleteHandler = (index: number, fieldId: any) => {
    setShowDeleteModal(true);
    setIndexForm(index);
    setFieldIdForm(fieldId);
  };
  const getModalText = (): Text => {
    const res = { date: '', injuryType: '' };
    const values = methods.getValues().caseDetails[indexForm];

    const isDisease =
      values?.caseTypeRelationId === diseaseId.toString() || false;

    const diseaseDiagnosisDate =
      values?.diseaseKindSection?.diseaseDiagnosisDate;
    const diseaseDiagnosisDateFormat =
      diseaseDiagnosisDate &&
      format(new Date(diseaseDiagnosisDate), 'dd/MM/yyyy');
    const injuryKindSection = values?.injuryKindSection?.date;
    const injuryKindSectionFormat =
      injuryKindSection && format(new Date(injuryKindSection), 'dd/MM/yyyy');

    if (isDisease) {
      if (diseaseDiagnosisDateFormat) {
        res.date = diseaseDiagnosisDateFormat;
      }
      res.injuryType = Dictionary[DICTIONARY.CASE_DETAILS].disease;
    } else {
      if (injuryKindSectionFormat) {
        res.date = injuryKindSectionFormat;
      }
      res.injuryType = Dictionary[DICTIONARY.CASE_DETAILS].injury;
    }
    return res;
  };

  const closeDeleteModalHandler = () => {
    setShowDeleteModal(false);
  };

  useEffect(() => {
    initFocus();
    initForm();
  }, []);

  /* Creating a ref object that will be used to store the id of the timeout. */
  const id = useRef<NodeJS.Timeout | undefined>(undefined);

  // normalize data
  const normalizeData = (): Partial<ICaseDetails>[] => {
    const data = methods.getValues();
    return data.caseDetails.map((caseDetail) => {
      if (!caseDetail) return {};
      return {
        ...caseDetail,
        areThereWitnessesOptionId: caseDetail.areThereWitnessesOptionId
          ? Number(caseDetail.areThereWitnessesOptionId)
          : null,
        militaryServiceSection: {
          ...caseDetail.militaryServiceSection,
          troopId: caseDetail.militaryServiceSection?.troopId
            ? caseDetail.militaryServiceSection?.troopId
            : null,
          medicalProfile: caseDetail.militaryServiceSection?.medicalProfile
            ? caseDetail.militaryServiceSection?.medicalProfile
            : null,
          serviceTypeId: caseDetail.militaryServiceSection?.serviceTypeId
            ? caseDetail.militaryServiceSection?.serviceTypeId
            : null,
          rankId: caseDetail.militaryServiceSection?.rankId
            ? caseDetail.militaryServiceSection?.rankId
            : null,
        },
        trafficAccidentSection: {
          ...caseDetail.trafficAccidentSection,
          vehicleDetailsSection: {
            ...caseDetail.trafficAccidentSection?.vehicleDetailsSection,
            ownershipVehicleId: caseDetail.trafficAccidentSection
              ?.vehicleDetailsSection?.ownershipVehicleId
              ? caseDetail.trafficAccidentSection?.vehicleDetailsSection
                  ?.ownershipVehicleId
              : null,
          },
          whatHaveYouDoDuringAccidentOptionId: caseDetail.trafficAccidentSection
            ?.whatHaveYouDoDuringAccidentOptionId
            ? Number(
                caseDetail.trafficAccidentSection
                  .whatHaveYouDoDuringAccidentOptionId
              )
            : null,
          compensationClaimSentOptionId: caseDetail.trafficAccidentSection
            ?.compensationClaimSentOptionId
            ? Number(
                caseDetail.trafficAccidentSection.compensationClaimSentOptionId
              )
            : null,
        },
        injuryKindSection: {
          ...caseDetail.injuryKindSection,
          investigatorOptionId: caseDetail.injuryKindSection
            ?.investigatorOptionId
            ? caseDetail.injuryKindSection?.investigatorOptionId
            : null,
          reportAttachedSelectedOptionId: caseDetail.injuryKindSection
            ?.reportAttachedSelectedOptionId
            ? Number(
                caseDetail.injuryKindSection?.reportAttachedSelectedOptionId
              )
            : null,
          whenInjuryHappanedOptionId: caseDetail.injuryKindSection
            ?.whenInjuryHappanedOptionId
            ? Number(caseDetail.injuryKindSection?.whenInjuryHappanedOptionId)
            : null,
          isInjuryDescribedInFile: convertStringBoolToBoolean(
            caseDetail.injuryKindSection?.isInjuryDescribedInFile
          ),
        },
        diseaseKindSection: {
          ...caseDetail.diseaseKindSection,
          isDiseaseDescribedInFile: convertStringBoolToBoolean(
            caseDetail.diseaseKindSection?.isDiseaseDescribedInFile
          ),
        },
        hospitalizationDetailsSection: caseDetail.hospitalizationDetailsSection,
        witnesses: caseDetail.witnesses?.map((witness) => ({
          ...witness,
          phonePrefixRelationId: witness?.phonePrefixRelationId
            ? witness?.phonePrefixRelationId
            : null,
          typeId: witness?.typeId ? witness?.typeId : null,
        })),
      } as Partial<ICaseDetails>;
    });
  };

  useEffect(() => {
    const subscription = methods.watch(async (data) => {
      if (initFlag.current) return;
      const normalizedData = normalizeData();
      debounce(id, putCaseDetails, normalizedData);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [methods.watch]);

  const forceSubmit = async () => {
    await putCaseDetails(normalizeData());
  };

  return (
    <>
      <Helmet>
        <title>
          {Dictionary[DICTIONARY.HEADER].title +
            ' - ' +
            Dictionary[DICTIONARY.CASE_DETAILS].title}
        </title>
      </Helmet>
      <HomePageFormLink save={forceSubmit} />
      <Stack alignItems='center' component={'main'} role={'main'}>
        <FormTitle title={Dictionary[DICTIONARY.CASE_DETAILS].title} />
        <TopDescription />
        <ValidationErrorContext.Provider
          value={{
            registerValidationErrorHandler,
            unregisterValidationErrorHandler,
          }}
        >
          <FormProvider {...methods}>
            <form
              onSubmit={methods.handleSubmit(
                onSubmit,
                handleValidationErrorsLocal
              )}
              style={{ width: isDesktop ? undefined : '100%' }}
            >
              {fields.map((field, index) => (
                <Fragment key={index}>
                  <IndexProvider index={index} fieldId={field.id}>
                    {index === 0 ? (
                      <ClassifiedDocumentsDeclaration />
                    ) : (
                      <AnotherCaseDetailHeader
                        deleteHandler={() =>
                          onDeleteHandler(index, fields[index].id)
                        }
                      />
                    )}
                    <FormSectionWrapper>
                      <FirstSection />
                      <SecondSection />
                    </FormSectionWrapper>
                    <ThirdSection />
                    <FourthSection />
                    <FifthSectionNew />
                    <SixthSectionNew />
                  </IndexProvider>
                </Fragment>
              ))}
              <AddCaseDetail append={appendHandler} save={forceSubmit} />
            </form>
            {showDeleteModal && (
              <DeleteModal
                isVisible={showDeleteModal}
                closeModal={closeDeleteModalHandler}
                deleteModal={() => removeHandler(indexForm, fieldIdForm)}
                texts={getModalText()}
              />
            )}
          </FormProvider>
        </ValidationErrorContext.Provider>
      </Stack>
    </>
  );
};
